// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Nav labels are WORDS ONLY. Never a count badge — counts belong inside the views themselves,
// where they can carry a unit and a caveat.

import type { Bundle } from './data';

export interface ViewContext {
  bundle: Bundle;
  container: HTMLElement;
}

export interface ViewDefinition {
  id: string;
  label: string;
  blurb: string;
  render: (context: ViewContext) => void | Promise<void>;
}

export const VIEWS: Array<{ id: string; label: string; blurb: string }> = [
  { id: 'average', label: 'The average lies', blurb: 'What share of a year arrives in a handful of hours' },
  { id: 'ledger', label: 'The ledger', blurb: 'Every detected episode, ranked two ways' },
  { id: 'map', label: 'Map', blurb: 'Replay an episode hour by hour, or see the air right now' },
  { id: 'propagation', label: 'Propagation', blurb: 'Did it travel, or appear everywhere at once' },
  { id: 'clock', label: 'Your clock', blurb: 'Which hours of which months are the bad ones here' },
  { id: 'rose', label: 'Where it blows from', blurb: 'Made here, or blown here' },
  { id: 'labels', label: 'Three labels', blurb: 'The same air, three official words for it' },
  { id: 'record', label: 'The record', blurb: 'What is measured, what is missing, who is absent' },
];

export function isView(id: string): boolean {
  return VIEWS.some((view) => view.id === id);
}

/**
 * The pipeline publishes an hourly detail file for the episodes with the widest reach, and only
 * those — the full set would be hundreds of megabytes. Anything ranked outside it has no replay
 * and no propagation matrix, so the views must lead somewhere else rather than opening an empty
 * screen. A click that looks interactive and does nothing is a defect.
 */
export const EPISODE_DETAIL_LIMIT = 200;

export function hasEpisodeDetail(rankReach: number): boolean {
  return rankReach <= EPISODE_DETAIL_LIMIT;
}
